import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import * as microsoftTeams from '@microsoft/teams-js'

interface TeamsContextValue {
  context: microsoftTeams.app.Context | null
  authToken: string | null
  isInitialized: boolean
}

const TeamsCtx = createContext<TeamsContextValue>({
  context: null,
  authToken: null,
  isInitialized: false,
})

export function useTeamsContext() {
  return useContext(TeamsCtx)
}

interface Props {
  children: ReactNode
}

export function TeamsProvider({ children }: Props) {
  const [state, setState] = useState<TeamsContextValue>({
    context: null,
    authToken: null,
    isInitialized: false,
  })

  useEffect(() => {
    microsoftTeams.app
      .initialize()
      .then(async () => {
        const context = await microsoftTeams.app.getContext()
        let authToken: string | null = null

        try {
          authToken = await microsoftTeams.authentication.getAuthToken()
        } catch {
          // SSO failed — user may need to sign in via MSAL fallback
        }

        setState({ context, authToken, isInitialized: true })
      })
      .catch(() => {
        // Running outside Teams (e.g. local browser dev)
        setState(s => ({ ...s, isInitialized: true }))
      })
  }, [])

  return <TeamsCtx.Provider value={state}>{children}</TeamsCtx.Provider>
}
